import React, { useState } from "react";
import {
  groupedSchools,
  isCustomSchoolValue,
  encodeCustomSchool,
  HOMESCHOOL_VALUE,
} from "../../constants/schools";
import register from "../../assets/register.svg";
import { API_BASE_URL } from "../../config";
import "./register.css";
import { Link as Linker, useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import Select from "react-select";
import { Alert } from "flowbite-react";
import { HiInformationCircle } from "react-icons/hi";
import axios from "axios";

const Register = () => {
  const selectStyle = {
    control: (baseStyles) => ({
      ...baseStyles,
      boxShadow: "none",
      borderColor: "transparent",
      outline: "none",
      "&:hover": { borderColor: "transparent" },
      fontSize: "0.75rem",
      minHeight: "44px",
    }),
    valueContainer: (base) => ({ ...base, outline: "none" }),
    input: (base) => ({ ...base, outline: "none", boxShadow: "none" }),
    singleValue: (base) => ({ ...base, fontSize: "0.75rem" }),
    placeholder: (base) => ({ ...base, fontSize: "0.75rem" }),
    option: (base) => ({ ...base, fontSize: "0.75rem" }),
  };

  const [showPasswordError, setShowPasswordError] = useState(false);
  const [showPasswordLengthError, setShowPasswordLengthError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showBlankError, setShowBlankError] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [backendErrorMessage, setBackendErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const navigate = useNavigate();
  const location = useLocation();

  const roles = [{ value: "student", label: "Student" }];

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    email2: "",
    password: "",
    password2: "",
    school: null,
    role: null,
    customSchoolName: "",
  });

  const isHomeschool = formData.school?.value === HOMESCHOOL_VALUE;
  const needsCustomSchoolName = isCustomSchoolValue(formData.school?.value);

  const resetAlerts = () => {
    setShowPasswordError(false);
    setShowPasswordLengthError(false);
    setShowEmailError(false);
    setShowBlankError(false);
    setBackendErrorMessage("");
  };

  const getNextPath = () => {
    const params = new URLSearchParams(location.search);
    const next = params.get("next");
    // Safety: only allow internal redirects
    if (next && next.startsWith("/")) return next;
    return "/subjects";
  };

  const handleSchoolSelect = (selectedOption) => {
    setShowBlankError(false);
    setBackendErrorMessage("");

    setFormData((prev) => {
      const nextVal = selectedOption?.value || null;
      const prevVal = prev.school?.value || null;

      const isNextCustom = isCustomSchoolValue(nextVal);
      const isSameCustomType = isNextCustom && nextVal === prevVal;

      return {
        ...prev,
        school: selectedOption,
        customSchoolName: isSameCustomType ? prev.customSchoolName : "",
      };
    });
  };

  const handleRoleSelect = (selectedOption) => {
    setShowBlankError(false);
    setBackendErrorMessage("");
    setFormData((prev) => ({ ...prev, role: selectedOption }));
  };

  const handleInputChange = (e, fieldName) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    resetAlerts();
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    resetAlerts();

    const email = formData.email.trim().toLowerCase();
    const email2 = formData.email2.trim().toLowerCase();

    if (
      formData.firstName.trim() === "" ||
      formData.lastName.trim() === "" ||
      email === "" ||
      email2 === "" ||
      formData.password === "" ||
      formData.password2 === "" ||
      !formData.school ||
      !formData.role ||
      (needsCustomSchoolName && formData.customSchoolName.trim() === "")
    ) {
      setShowBlankError(true);
      return;
    }

    if (!emailRegex.test(email)) {
      setBackendErrorMessage("Please enter a valid email address.");
      setShowEmailError(true);
      return;
    }

    if (email !== email2) {
      setBackendErrorMessage("Emails do not match.");
      setShowEmailError(true);
      return;
    }

    if (formData.password.length < 8) {
      setShowPasswordLengthError(true);
      return;
    }

    if (formData.password !== formData.password2) {
      setShowPasswordError(true);
      return;
    }

    // Same encoding the profile screen writes, so a school typed at sign-up and one
    // typed later are stored identically and both decode back into the form.
    const schoolToSend = needsCustomSchoolName
      ? encodeCustomSchool(formData.customSchoolName, formData.school?.value)
      : formData.school?.value || "";

    const dataToSend = {
      first_name: formData.firstName.trim(),
      last_name: formData.lastName.trim(),
      email: email,
      email_confirmation: email2,
      password: formData.password,
      school: schoolToSend,
      role: formData.role?.value || "",
    };

    setIsLoading(true);

    try {
      // 1) Register
      const registerResp = await axios.post(`${API_BASE_URL}/api/register`, dataToSend, {
        headers: { "Content-Type": "application/json" },
      });

      // Handle legacy response shapes
      if (registerResp?.data === "Email already exists") {
        setBackendErrorMessage("Email already exists");
        setShowEmailError(true);
        return;
      }

      if (registerResp?.data?.success === false) {
        const msg =
          registerResp?.data?.error ||
          registerResp?.data?.message ||
          (Array.isArray(registerResp?.data?.errors)
            ? registerResp.data.errors.join(", ")
            : "Registration failed.");
        setBackendErrorMessage(msg);
        setShowEmailError(true);
        return;
      }

      // 2) Auto-login (creates auth session + returns token/user)
      const loginResp = await axios.post(`${API_BASE_URL}/api/login`, {
        email,
        password: formData.password,
      });

      const { user, token } = loginResp.data || {};
      if (!token || !user) {
        setBackendErrorMessage("Registration succeeded but auto-login failed. Please sign in.");
        setShowEmailError(true);
        return;
      }

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("auth_token", token);

      // 3) Success UI + redirect
      setShowSuccessAlert(true);
      const nextPath = getNextPath();

      setTimeout(() => {
        navigate(nextPath);
      }, 500);
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      const msg =
        data?.error ||
        data?.message ||
        (Array.isArray(data?.errors) ? data.errors.join(", ") : null) ||
        "Registration failed. Please try again.";

      setBackendErrorMessage(
        status === 422 ? msg : "Something went wrong. Please try again."
      );
      setShowEmailError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="struct">
      {/* Logo */}
      <a
        href="/"
        className="absolute top-4 left-4 sm:left-7 z-10 flex items-center gap-2 no-underline hover:no-underline before:content-none after:content-none"
      >
        <img
          src={logo}
          className="h-8 w-8 sm:h-9 sm:w-9 block"
          alt="EduBahamas logo"
        />
        <h2 className="font-bold text-[#1896b9] text-lg sm:text-xl md:text-[22px] lg:text-[25px] leading-none">
          EduBahamas
        </h2>
      </a>

      <div className="w-full min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-gray-100">
        {/* Left Side - Form */}
        <div className="flex flex-col items-center justify-center w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6 lg:py-12 order-2 lg:order-1 overflow-y-auto pt-20 sm:pt-24 lg:pt-12">
          <form className="w-full max-w-lg" onSubmit={handleRegister}>
            {/* Alerts */}
            {showPasswordError && (
              <Alert
                className="w-full mb-4"
                color="failure"
                icon={HiInformationCircle}
                onDismiss={() => setShowPasswordError(false)}
              >
                Passwords do not match
              </Alert>
            )}

            {showPasswordLengthError && (
              <Alert
                className="w-full mb-4"
                color="failure"
                icon={HiInformationCircle}
                onDismiss={() => setShowPasswordLengthError(false)}
              >
                Password is too short. Password should be at least 8 characters.
              </Alert>
            )}

            {showEmailError && (
              <Alert
                className="w-full mb-4"
                color="failure"
                icon={HiInformationCircle}
                onDismiss={() => setShowEmailError(false)}
              >
                {backendErrorMessage || "Registration failed"}
              </Alert>
            )}

            {showBlankError && (
              <Alert
                className="w-full mb-4"
                color="failure"
                icon={HiInformationCircle}
                onDismiss={() => setShowBlankError(false)}
              >
                Missing information. Please fill out all sections
              </Alert>
            )}

            {showSuccessAlert && (
              <Alert
                className="w-full mb-4"
                color="success"
                icon={HiInformationCircle}
                onDismiss={() => setShowSuccessAlert(false)}
              >
                Registration Successful - Signing you in!
              </Alert>
            )}

            <h2 className="text-[#1896b9] text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 md:mb-5">
              Register
            </h2>

            {/* Name Fields */}
            <input
              className="w-full text-black focus:ring-transparent border border-gray-200 outline-none focus:shadow-lg shadow-md py-5 px-4 rounded-md"
              type="text"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) => handleInputChange(e, "firstName")}
            />

            <input
              className="w-full focus:ring-transparent border border-gray-200 outline-none focus:shadow-lg shadow-md py-5 px-4 rounded-md"
              type="text"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) => handleInputChange(e, "lastName")}
            />

            {/* Email */}
            <div className="mb-5">
              <input
                className="w-full focus:ring-transparent border border-gray-200 focus:shadow-lg shadow-md py-5 px-4 rounded-md"
                type="email"
                placeholder="Email"
                style={{ outline: "none" }}
                value={formData.email}
                onChange={(e) => handleInputChange(e, "email")}
                required
                onBlur={() => {
                  const normalized = formData.email.trim().toLowerCase();
                  if (normalized && !emailRegex.test(normalized)) {
                    setBackendErrorMessage("Please enter a valid email address.");
                    setShowEmailError(true);
                  }
                }}
              />
            </div>

            {/* Confirm Email */}
            <div className="mb-5">
              <input
                className="w-full focus:ring-transparent border border-gray-200 focus:shadow-lg shadow-md py-5 px-4 rounded-md"
                type="email"
                placeholder="Confirm Email"
                style={{ outline: "none" }}
                value={formData.email2}
                onChange={(e) => handleInputChange(e, "email2")}
                required
                onBlur={() => {
                  const e1 = formData.email.trim().toLowerCase();
                  const e2 = formData.email2.trim().toLowerCase();

                  if (e2 && !emailRegex.test(e2)) {
                    setBackendErrorMessage("Please enter a valid email address.");
                    setShowEmailError(true);
                    return;
                  }

                  if (e1 && e2 && e1 !== e2) {
                    setBackendErrorMessage("Emails do not match.");
                    setShowEmailError(true);
                  }
                }}
              />
            </div>

            {/* Password */}
            <div className="mb-5">
              <input
                className="w-full focus:ring-transparent border border-gray-200 outline-none focus:shadow-lg shadow-md py-5 px-4 rounded-md"
                type="password"
                placeholder="Password"
                value={formData.password}
                onChange={(e) => handleInputChange(e, "password")}
                required
              />
            </div>

            {/* Confirm Password */}
            <div className="mb-5">
              <input
                className="w-full focus:ring-transparent border border-gray-200 outline-none focus:shadow-lg shadow-md py-5 px-4 rounded-md"
                type="password"
                placeholder="Confirm Password"
                value={formData.password2}
                onChange={(e) => handleInputChange(e, "password2")}
                required
              />
            </div>

            {/* School and Role Selects */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="w-full sm:w-1/2">
                <label className="text-gray-500 text-sm block mb-2">
                  Select a School:
                </label>

                <Select
                  className="shadow-md"
                  options={groupedSchools}
                  isSearchable
                  isClearable
                  value={formData.school}
                  onChange={handleSchoolSelect}
                  classNamePrefix="react-select"
                  styles={selectStyle}
                />

                {needsCustomSchoolName && (
                  <div className="mt-3">
                    <label className="text-gray-500 text-sm block mb-2">
                      {isHomeschool ? "Homeschool name:" : "School name:"}
                    </label>
                    <input
                      className="w-full focus:ring-transparent border border-gray-200 outline-none focus:shadow-lg shadow-md py-5 px-4 rounded-md text-sm"
                      type="text"
                      placeholder={isHomeschool ? "Enter your homeschool name" : "Enter your school name"}
                      value={formData.customSchoolName}
                      onChange={(e) => handleInputChange(e, "customSchoolName")}
                    />
                  </div>
                )}
              </div>

              <div className="w-full sm:w-1/2">
                <label className="text-gray-500 text-sm block mb-2">
                  Select a Role:
                </label>

                <Select
                  className="shadow-md"
                  options={roles}
                  isSearchable
                  isClearable
                  value={formData.role}
                  onChange={handleRoleSelect}
                  classNamePrefix="react-select"
                  styles={selectStyle}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center items-center mb-6">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full sm:w-4/5 md:w-3/4 bg-[#1896b9] hover:bg-[#126e87] rounded-md py-3 text-sm sm:text-base font-medium text-white transition duration-200 ${
                  isLoading ? "opacity-70 cursor-not-allowed" : ""
                }`}
                style={{
                  lineHeight: "1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isLoading ? "Creating Account..." : "Register"}
              </button>
            </div>

            {/* Sign In Link */}
            <Linker className="hover:bg-transparent hover:before:w-0" to="/signin">
              <p className="text-gray-600 flex justify-center items-center text-sm hover:underline">
                Already have an account? &nbsp; <b>Sign In</b>
              </p>
            </Linker>
          </form>
        </div>

        {/* Right Side */}
        <div className="hidden lg:flex justify-center items-center text-center w-full h-full bg-[#1896b9] p-8 order-1 lg:order-2">
          <div className="hidden lg:flex justify-center items-center text-center w-full h-full bg-[#1896b9] p-8 order-1 lg:order-2">
            <blockquote className="text-3xl xl:text-4xl 2xl:text-5xl italic font-semibold text-white px-4 xl:px-8">
              <svg
                className="w-8 h-8 xl:w-10 xl:h-10 text-white mb-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 18 14"
              >
                <path d="M6 0H2a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3H2a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Zm10 0h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4v1a3 3 0 0 1-3 3h-1a1 1 0 0 0 0 2h1a5.006 5.006 0 0 0 5-5V2a2 2 0 0 0-2-2Z" />
              </svg>
              <p className="text-left leading-relaxed mb-4">
                "Education is not preparation for life, education is life itself."
              </p>
              <p className="text-right">— John Dewey</p>
            </blockquote>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
